import { Link } from "react-router-dom";
import SunAccent from "@/components/feature/SunAccent";
import Reveal from "@/components/base/Reveal";
import { farmCoverAlt } from "@/lib/imageAlt";
import { isFarmOpen, type Farm } from "@/mocks/farms";

/**
 * Ranks the other farms by how closely they relate to the current one —
 * farms in the same village score highest, followed by farms that share
 * the same crop types — so visitors get genuinely nearby suggestions.
 */
function getRelatedFarms(all: Farm[], farm: Farm, limit = 3): Farm[] {
  return all
    .filter((item) => item.slug !== farm.slug)
    .map((item) => ({
      item,
      score:
        (item.village === farm.village ? 2 : 0) +
        item.types.filter((type) => farm.types.includes(type)).length,
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export default function FarmDetailRelated({ farm, farms }: { farm: Farm; farms: Farm[] }) {
  const related = getRelatedFarms(farms, farm);

  if (related.length === 0) {
    return null;
  }

  return (
    <section className="sun-glaze bg-background-100 py-20 md:py-24">
      <div className="w-full px-4 md:px-8">
        <Reveal className="max-w-3xl mb-10 md:mb-12">
          <div className="flex items-center gap-3 mb-5">
            <SunAccent circleClassName="w-8 h-8" iconClassName="text-base" /><span className="h-px w-10 bg-accent-500"></span>
            <span className="text-xs font-semibold tracking-[0.24em] uppercase text-accent-600">
              Nearby Farms
            </span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl leading-tight text-foreground-950 font-semibold">
            Related Farms You Can Hop Between
          </h2>
          <p className="mt-5 text-base md:text-lg text-foreground-700 leading-relaxed">
            Farms close to {farm.name} — in {farm.village} and around the delta with similar crops.
            Pair two visits into one trip and see the Godavari delta from more than one field.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {related.map((item, index) => (
            <Reveal key={item.slug} delay={index * 80} className="h-full">
              <article className="h-full flex flex-col bg-background-50 rounded-lg border border-background-200 overflow-hidden transition-colors hover:border-accent-300">
                <Link
                  to={`/farms/${item.slug}`}
                  className="relative block w-full h-44 md:h-48 overflow-hidden cursor-pointer"
                  aria-label={`View ${item.name}`}
                >
                  <img
                    src={item.image}
                    width={1200}
                    height={900}
                    alt={farmCoverAlt(item)}
                    title={`${item.name} - ${item.crop} farm in ${item.village}`}
                    className="w-full h-full object-cover object-top"
                    decoding="async"
                  />
                  <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-background-50/90 text-foreground-900 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <i className="ri-map-pin-2-line text-accent-600"></i>
                    {item.village}
                  </span>
                  {!isFarmOpen(item) && (
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1.5 bg-secondary-900/90 text-background-50 text-xs font-semibold px-3 py-1.5 rounded-full">
                      <i className="ri-time-line"></i>
                      Reopening soon
                    </span>
                  )}
                </Link>

                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-heading text-lg md:text-xl text-foreground-950 font-semibold">
                    <Link
                      to={`/farms/${item.slug}`}
                      className="hover:text-primary-700 transition-colors cursor-pointer"
                    >
                      {item.name}
                    </Link>
                  </h3>
                  <p className="text-xs text-foreground-600 mt-1.5">
                    {item.crop} · {item.area} · {item.established}
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed mt-3 flex-1">
                    {item.summary}
                  </p>

                  <div className="flex flex-wrap gap-2 mt-4">
                    {item.highlights.slice(0, 2).map((highlight) => (
                      <span
                        key={highlight}
                        className="text-xs font-medium bg-secondary-100 text-secondary-900 border border-secondary-200 px-2.5 py-1 rounded-full"
                      >
                        {highlight}
                      </span>
                    ))}
                  </div>

                  <Link
                    to={`/farms/${item.slug}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    View full farm
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}